import * as React from "react";
import ReactDOM from "react-dom";
import { Button, DatePicker, Input, Select } from "@jhool-io/fe-components";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

import { useQueryClient } from "@tanstack/react-query";
import {
    useLocation,
    useNavigate,
    useParams,
    useSearchParams,
} from "react-router-dom";
import { useDropzone } from "react-dropzone";
import { IAddManualRemit } from "../../types/remits.types";
import { useFetchInsuranceProviders } from "../../../../hooks/queries/client";
import {
    cn,
    handleFormatDatePickerValue,
    removeEnumUnderscore,
} from "../../../../utils/helpers";
import {
    useAddManualRemit,
    useAddRemitClaims,
} from "../../hooks/remits.mutations";
import useToast from "../../../../hooks/useToast";
import FolderOpenIcon from "../../../../components/Icons/FolderOpen";
import TimesIcon from "../../../../icons/Times";

type SubmitTypes = "save" | "save-continue" | null;

interface Props {
    claimId?: string;
    onFormSubmit: () => void;
    submitType: SubmitTypes;
}

type Options = {
    label: React.ReactNode;
    value: string;
};

const schema = yup.object({
    amount: yup.number().required("Please enter a valid amount"),
    no_of_claims_payments: yup.number(),
    remit_date: yup.date().required("Remit date is required"),
    insurance_provider_id: yup
        .string()
        .required("Insurance provider is required"),
    remit_batch_payment_ref: yup
        .string()
        .required("Remit payment ref is required"),
});

export default function ManualRemitForm({ claimId, onFormSubmit }: Props) {
    const [searchParams] = useSearchParams();

    const activeClaimId = searchParams.get("active_claim_id");

    const insuranceProviders = useFetchInsuranceProviders();
    const [remitDate, setRemitDate] = React.useState<Date | null>(null);
    const [selectedFiles, setSelectedFiles] = React.useState<File[] | null>(
        null
    );

    const addManualRemit = useAddManualRemit();

    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const location = useLocation();

    const params = useParams();
    const noteId = params.noteId || "";
    // Toast for success and error states
    const { toast } = useToast();

    const addRemitClaims = useAddRemitClaims();

    const currentAppUrl = location.pathname;

    const isRemitsListPage = currentAppUrl?.startsWith("/remits");

    const {
        register,
        handleSubmit,
        control,
        formState: { errors },
    } = useForm<IAddManualRemit>({
        resolver: yupResolver(schema),
        mode: "onChange",
    });

    // Function to call when file(s) are dropped in dropzone
    const onDrop = React.useCallback(
        (acceptedFiles: File[]) => {
            if (selectedFiles && selectedFiles.length > 0) {
                setSelectedFiles([...acceptedFiles, ...selectedFiles]);
            } else setSelectedFiles([...acceptedFiles]);
        },
        [selectedFiles]
    );

    // Initialize the useDropzone hook
    const { getRootProps, isDragActive } = useDropzone({
        onDrop,
        maxFiles: 1,
        accept: {
            "application/pdf": [".pdf"],
            "image/*": [".png", ".webp", ".jpeg", ".jpg", ".avif"],
        },
    });

    // Function for setting selected files
    const handleSetSelectedFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            if (selectedFiles && selectedFiles.length > 0) {
                setSelectedFiles([...e.target.files, ...selectedFiles]);
            } else setSelectedFiles([...e.target.files]);
        }
    };

    const handleRemoveSelectedFile = (filename: string) => {
        if (selectedFiles) {
            const filesArr = [...selectedFiles].filter(
                (file) => file.name !== filename
            );
            if (filesArr.length === 0) setSelectedFiles(null);
            else setSelectedFiles(filesArr);
        }
    };

    const formData = new FormData();

    const onSubmit = (data: IAddManualRemit) => {
        const claimIdToLink = activeClaimId ?? claimId ?? "";

        if (!isRemitsListPage && !claimIdToLink) {
            toast({
                mode: "error",
                message: "Could not determine which claim to link.",
            });
            return;
        }

        formData.append(
            "remit_batch_payment_ref",
            data.remit_batch_payment_ref
        );
        formData.append("insurance_provider_id", data.insurance_provider_id);
        formData.append(
            "no_of_claims_payments",
            String(data.no_of_claims_payments)
        );
        formData.append(
            "remit_date",
            handleFormatDatePickerValue(data.remit_date)
        );
        formData.append("amount", String(data.amount));
        if (selectedFiles && selectedFiles.length > 0) {
            for (let i = 0; i < [...selectedFiles].length; i += 1) {
                formData.append("batch_remit_eob_file", [...selectedFiles][i]);
            }
        }

        addManualRemit.mutate(formData, {
            onSuccess: async (res) => {
                toast({
                    mode: "success",
                    message: res.message || "Remit added successfully.",
                });
                queryClient.invalidateQueries({
                    queryKey: [
                        "batch-remits",
                        { batch_remit_id: res.remittance_batch_id },
                    ],
                });
                if (isRemitsListPage) {
                    onFormSubmit();
                    navigate(`/remits/${res.remittance_batch_id}/claims`);
                }

                if (!isRemitsListPage) {
                    addRemitClaims.mutate(
                        {
                            claim_ids: [claimIdToLink],
                            remit_id: res.remittance_batch_id,
                        },
                        {
                            onSuccess: async (response) => {
                                toast({
                                    mode: "success",
                                    message:
                                        response.message || "Remit claim added",
                                });
                                await queryClient.invalidateQueries({
                                    queryKey: [
                                        "remit-claims",
                                        { note_id: noteId },
                                    ],
                                });
                                await queryClient.invalidateQueries([
                                    "batch-remits",
                                    {
                                        batch_remit_id: res.remittance_batch_id,
                                    },
                                ]);

                                onFormSubmit();
                            },
                            onError: (err) => {
                                onFormSubmit();
                                toast({
                                    mode: "error",
                                    message:
                                        err.response?.data.message ||
                                        "Could not add remit claim",
                                });
                            },
                        }
                    );
                }
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message || "Could not add remit.",
                });
            },
        });
    };

    const insuranceProvidersForSelect: Options[] = insuranceProviders.data
        ? insuranceProviders.data.data
              .sort((a, b) => a.alias.localeCompare(b.alias))
              .map((provider) => ({
                  value: provider.insurance_provider_id,
                  label: removeEnumUnderscore(provider.alias).toUpperCase(),
              }))
        : [];

    return (
        <form id="add-remit-form" onSubmit={handleSubmit(onSubmit)}>
            <div className="fg fg-space-between two flex">
                <Input
                    label="Remit Payment Ref"
                    {...register("remit_batch_payment_ref")}
                    hasError={!!errors.remit_batch_payment_ref}
                    errorText={errors.remit_batch_payment_ref?.message}
                    placeholder="Enter remit payment ref"
                    isRequired
                />

                <Input
                    label="Remit Amount"
                    {...register("amount")}
                    hasError={!!errors.amount}
                    errorText={
                        errors.amount?.type === "typeError"
                            ? "This field is required and must be a number"
                            : "Amount must be greater than 0"
                    }
                    showCurrency
                    placeholder="$ USD 00"
                    isRequired
                />
            </div>
            <div className="fg fg-space-between two flex">
                <Controller
                    name="insurance_provider_id"
                    control={control}
                    render={({ field }) => (
                        <Select
                            label="Insurance"
                            wrapperClass="select"
                            options={insuranceProvidersForSelect}
                            isDisabled={insuranceProviders.isLoading}
                            onChange={(val) => {
                                field.onChange((val as Options).value);
                            }}
                            hasError={!!errors.insurance_provider_id}
                            errorText={errors.insurance_provider_id?.message}
                            isLongListInDialog
                            isSearchable
                            isRequired
                        />
                    )}
                />

                <Input
                    label="No of claims payment"
                    {...register("no_of_claims_payments")}
                    hasError={!!errors?.no_of_claims_payments}
                    placeholder="Enter number of claims payment"
                    type="number"
                    errorText={
                        errors.no_of_claims_payments?.type === "typeError"
                            ? "This field must be a number"
                            : errors.no_of_claims_payments?.message
                    }
                    defaultValue={0}
                />
            </div>
            <div className="fg">
                <Controller
                    name="remit_date"
                    control={control}
                    render={({ field }) => (
                        <DatePicker
                            label="Check date"
                            placeholderText="MM/DD/YYYY"
                            isClearable
                            onChange={(date) => {
                                field.onChange(date);
                                setRemitDate(date);
                            }}
                            selected={remitDate}
                            hasError={!!errors.remit_date}
                            errorText={
                                errors.remit_date?.type === "typeError"
                                    ? "invalid date value"
                                    : errors.remit_date?.message
                            }
                            popperClassName="!z-[40]"
                            popperContainer={({ children }) =>
                                ReactDOM.createPortal(children, document.body)
                            }
                            popperProps={{
                                strategy: "fixed",
                                modifiers: [
                                    {
                                        name: "zIndex",
                                        options: {
                                            zIndex: 40,
                                        },
                                    },
                                ],
                            }}
                            isRequired
                        />
                    )}
                />
            </div>

            <div
                className={cn(
                    "flex w-full items-center gap-x-[--spacing-items]  border border-dashed border-[--color-text-link] rounded-lg px-[--padding-card-x] py-[--padding-card-y]",
                    {
                        "opacity-20": selectedFiles?.length === 1,
                        "border-[2px] border-dashed border-stroke":
                            isDragActive,
                    }
                )}
                {...getRootProps()}
            >
                <div className="p-16 bg-[rgba(204,250,233,0.50)] rounded-md shrink-0">
                    <FolderOpenIcon />
                </div>
                <div className="flex flex-col">
                    <div className="text-base font-medium">
                        <label
                            htmlFor="file-upload"
                            className="font-semibold text-textLink"
                        >
                            Click to upload remit eob{" "}
                            <input
                                className="w-full absolute left-[-99999px]"
                                id="file-upload"
                                name="file-upload"
                                type="file"
                                onChange={handleSetSelectedFiles}
                                disabled={selectedFiles?.length === 1}
                                max="1"
                            />
                        </label>
                        or drag your file here
                    </div>
                    <p className="text-caption font-normal">
                        Maximum file size of 10 MB: .png, .jpg, .jpeg
                    </p>
                </div>
            </div>

            {addManualRemit.uploadProgress > 0 && (
                <div className="mt-[--spacing-items] h-2 bg-stroke rounded-md">
                    <div
                        role="progressbar"
                        aria-label="file upload progress value"
                        aria-valuenow={addManualRemit.uploadProgress}
                        style={{
                            width: `${addManualRemit.uploadProgress}%`,
                        }}
                        className="h-full rounded-r8 bg-primary"
                    />
                </div>
            )}

            <div className="mt-[--spacing-form-items]">
                {selectedFiles &&
                    selectedFiles.map((file, i) => (
                        <div
                            key={`${file.name} - ${i + 1}`}
                            className="flex items-center justify-between w-full [border:1px_solid_var(--color-stroke)] rounded-md px-[--padding-card-x] py-[--padding-card-y] my-[--spacing-items]"
                        >
                            <div className="text-caption text-xs text-textSec">
                                {file.name}
                            </div>
                            <Button
                                variant="can-hover"
                                aria-label="delete icon"
                                size="auto"
                                onClick={() =>
                                    handleRemoveSelectedFile(file.name)
                                }
                            >
                                <TimesIcon />
                            </Button>
                        </div>
                    ))}
            </div>
        </form>
    );
}
